<template>
  <section class="car-search-section">
    <div class="container">
      <!-- Main Header -->
      <div
        class="section-header"
        data-aos="zoom-in"
        data-aos-duration="800"
        data-aos-delay="100"
      >
        <h5 class="pre-title" data-aos="fade-down" data-aos-delay="200">
          Find Your Perfect Match
        </h5>
        <h1 class="section-title" data-aos="fade-up" data-aos-delay="300">
          Car Shopping Your Way
        </h1>
        <div
          class="title-underline"
          data-aos="slide-right"
          data-aos-delay="400"
        ></div>
        <p class="section-tagline" data-aos="fade-up" data-aos-delay="500">
          Discover your dream vehicle with our powerful search tools - tailored
          to your preferences and budget.
        </p>
      </div>

      <!-- Search form -->
      <div
        class="search-form-container"
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="600"
      >
        <div class="search-form">
          <div class="form-group" data-aos="fade-up-right" data-aos-delay="700">
            <label for="make-select">Car Make</label>
            <div class="select-wrapper">
              <select v-model="make" id="make-select" class="form-control">
                <option value="">Select Make</option>
                <option
                  v-for="manufacturer in manufacturers"
                  :key="manufacturer.id"
                  :value="manufacturer.name"
                >
                  {{ manufacturer.name }}
                </option>
              </select>
              <div class="select-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>

          <div class="form-group" data-aos="fade-up" data-aos-delay="800">
            <label for="price-select">Price Range</label>
            <div class="select-wrapper">
              <select
                v-model="priceRange"
                id="price-select"
                class="form-control"
              >
                <option value="">Select Price Range</option>
                <option value="5000-10000">$5,000 - $10,000</option>
                <option value="10000-20000">$10,000 - $20,000</option>
                <option value="20000-30000">$20,000 - $30,000</option>
                <option value="30000+">$30,000+</option>
              </select>
              <div class="select-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>

          <div class="form-group" data-aos="fade-up-left" data-aos-delay="900">
            <label for="engine-select">Engine Type</label>
            <div class="select-wrapper">
              <select
                v-model="engineType"
                id="engine-select"
                class="form-control"
              >
                <option value="">Select Engine Type</option>
                <option
                  v-for="engine in engineTypes"
                  :key="engine.id"
                  :value="engine.type"
                >
                  {{ engine.type }}
                </option>
              </select>
              <div class="select-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>

          <div
            class="button-container"
            data-aos="fade-up"
            data-aos-delay="1000"
          >
            <button
              class="search-button"
              @click="searchCars"
              :disabled="isSearching"
            >
              <span v-if="!isSearching">Find Cars</span>
              <span v-else class="searching-text">Searching</span>
              <span class="button-icon" :class="{ searching: isSearching }">
                <svg
                  v-if="!isSearching"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                </svg>
                <svg
                  v-else
                  class="spinner"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              </span>
            </button>
          </div>
        </div>

        <!-- Results -->
        <transition name="fade">
          <div v-if="searchResults.length" class="car-results">
            <div
              v-for="car in searchResults"
              :key="car.id"
              class="car-card"
              @click="goToCarPage(car.id)"
            >
              <div class="car-image-wrapper">
                <img
                  :src="car.image"
                  :alt="`${car.manufacturer} ${car.model}`"
                  class="car-image"
                />
                <!-- <span v-if="car.has_sale" class="hot-deal-badge">Hot Deal</span> -->
              </div>
              <h3 class="car-name">
                {{ car.manufacturer }} {{ car.model }} {{ car.year }}
              </h3>
              <p class="car-price">
                ${{ (car.has_sale ? car.price : car.price).toLocaleString() }}
                <span v-if="car.has_sale" class="original-price"
                  >${{ car.old_price.toLocaleString() }}</span
                >
              </p>
              <p class="car-details">
                {{
                  car.mileage_miles
                    ? car.mileage_miles.toLocaleString() + " miles"
                    : "Mileage N/A"
                }}
                • {{ car.engine_type }}
              </p>
            </div>
          </div>
          <div v-else-if="searched && !isSearching" class="no-results">
            <div class="no-results-container">
              <svg
                class="no-results-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-11.5 7.5 8.38 8.38 0 0 1-7.5-11.5A8.38 8.38 0 0 1 13.5 3a8.38 8.38 0 0 1 7.5 8.5z"
                />
                <line x1="16" y1="16" x2="21" y2="21" />
              </svg>
              <h3 class="no-results-title">No Cars Found</h3>
              <p class="no-results-text">
                Sorry, we couldn’t find any cars matching your criteria. Try
                adjusting your filters!
              </p>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import API_CONFIG from "@/config/api.config.js"; // Import the config

export default {
  name: "CarSearchComponent",
  data() {
    return {
      make: "",
      priceRange: "",
      engineType: "",
      isSearching: false,
      searched: false,
      manufacturers: [],
      engineTypes: [],
      searchResults: [],
      baseUrl: API_CONFIG.baseUrl,
    };
  },
  mounted() {
    AOS.init({
      once: false,
      offset: 50,
    });
    this.fetchSearchParams();
  },
  methods: {
    async fetchSearchParams() {
      try {
        const response = await axios.get(`${this.baseUrl}/api/search_params`);
        if (response.data.success) {
          this.manufacturers = response.data.manufacturers;
          this.engineTypes = response.data.engine_types;
        }
      } catch (error) {
        console.error("Error fetching search params:", error);
      }
    },
    async searchCars() {
      this.isSearching = true;
      this.searched = true;
      this.searchResults = [];

      try {
        const params = {};
        if (this.make) params.manufacturer = this.make;
        if (this.engineType) params.engine_type = this.engineType;
        if (this.priceRange) params.price_range = this.priceRange;

        const response = await axios.get(`${this.baseUrl}/api/cars/search`, {
          params,
        });
        if (response.data.success) {
          this.searchResults = response.data.cars.map((car) => ({
            ...car,
            image: `${this.baseUrl}${car.image}`,
          }));
        }
      } catch (error) {
        console.error("Error searching cars:", error);
      } finally {
        this.isSearching = false;
      }
    },
    goToCarPage(carId) {
      this.$router.push({ path: `/cars/${carId}`, hash: "#top" }).then(() => {
        window.scrollTo(0, 0);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.car-search-section {
  position: relative;
  padding: 100px 0;
  overflow: hidden;
  color: #1a1a2e;
  background-color: #ffffff;

  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
  }

  .section-header {
    text-align: center;
    margin-bottom: 3rem;

    .pre-title {
      text-transform: uppercase;
      letter-spacing: 3px;
      font-size: 0.9rem;
      font-weight: 500;
      color: #ee800a;
      margin-bottom: 1rem;
    }

    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: #09283f;
      letter-spacing: -0.5px;
      line-height: 1.1;
    }

    .title-underline {
      width: 80px;
      height: 3px;
      background: linear-gradient(to right, #ee800a, #ff7043);
      margin: 0 auto 1.5rem;
      border-radius: 2px;
    }

    .section-tagline {
      font-size: 1.3rem;
      font-weight: 300;
      line-height: 1.6;
      max-width: 700px;
      margin: 0 auto;
      color: #4a4a4a;
    }
  }

  .search-form-container {
    margin-bottom: 3rem;
  }

  .search-form {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
    position: relative;

    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #09283f, #ee800a);
      border-radius: 12px 12px 0 0;
    }

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }

    .form-group {
      display: flex;
      flex-direction: column;

      label {
        font-size: 0.9rem;
        font-weight: 600;
        color: #09283f;
        margin-bottom: 0.5rem;
      }

      .select-wrapper {
        position: relative;
      }

      .select-icon {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        width: 20px;
        height: 20px;
        color: #09283f;
      }
    }

    .form-control {
      width: 100%;
      padding: 12px 16px;
      font-size: 1rem;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      background-color: #f9f9f9;
      color: #333;
      appearance: none;
      transition: all 0.3s ease;

      &:focus {
        outline: none;
        border-color: #ee800a;
        box-shadow: 0 0 0 3px rgba(238, 128, 10, 0.15);
      }
    }

    .button-container {
      display: flex;
      align-items: flex-end;
    }

    .search-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: 14px 20px;
      background: linear-gradient(135deg, #ee800a, #ff7043);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.4s ease;
      box-shadow: 0 10px 20px rgba(238, 128, 10, 0.2);

      &:disabled {
        opacity: 0.8;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        box-shadow: 0 10px 25px rgba(9, 40, 63, 0.3);
        transform: scale(1.05);
      }

      .button-icon {
        margin-left: 10px;
        &.searching {
          animation: bounce 1.5s infinite;
        }
      }

      .spinner {
        animation: spin 1s linear infinite;
      }
    }
  }

  .car-results {
    margin-top: 2rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
    gap: 1.5rem;
  }

  .car-card {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.4s ease;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
    cursor: pointer;
    position: relative;

    &:hover {
      transform: translateY(-12px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
      .car-image {
        transform: scale(1.08);
      }
      .car-name {
        color: #ee800a;
      }
    }

    .hot-deal-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      background: #ff4444;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  }

  .car-image-wrapper {
    width: 100%;
    height: 200px;
    overflow: hidden;
    position: relative;
  }

  .car-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  .car-name {
    padding: 1.25rem 1.5rem 0.5rem;
    font-size: 1.2rem;
    font-weight: 700;
    color: #09283f;
    margin: 0;
    transition: color 0.3s ease;
  }

  .car-price {
    padding: 0 1.5rem;
    margin: 0;
    font-size: 1rem;
    color: #555;
    font-weight: 600;
    display: flex;
    align-items: center;

    &:before {
      content: "";
      display: inline-block;
      width: 6px;
      height: 6px;
      background-color: #ee800a;
      border-radius: 50%;
      margin-right: 8px;
    }

    .original-price {
      text-decoration: line-through;
      color: #999;
      font-size: 0.9rem;
      margin-left: 0.5rem;
    }
  }

  .car-details {
    padding: 0.5rem 1.5rem 1.5rem;
    color: #666;
    font-size: 0.9rem;
    margin: 0;
  }

  .no-results {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;

    .no-results-container {
      background: #f9f9f9;
      border-radius: 12px;
      padding: 2rem;
      text-align: center;
      max-width: 500px;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
      border: 1px solid #e0e0e0;
    }

    .no-results-icon {
      color: #ee800a;
      margin-bottom: 1rem;
      opacity: 0.8;
    }

    .no-results-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: #09283f;
      margin-bottom: 0.75rem;
    }

    .no-results-text {
      font-size: 1rem;
      color: #666;
      line-height: 1.5;
      margin-bottom: 1.5rem;
    }

    .reset-button {
      padding: 10px 20px;
      background: #09283f;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: #ee800a;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(238, 128, 10, 0.3);
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
}
</style>
